/**
 * Main App Component
 * 
 * Root component with routing and authentication state management
 */

import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuthStore } from './stores/authStore'
import { useEffect, useState } from 'react'

// Pages
import LoginPage from './pages/LoginPage'
import AuthCallbackPage from './pages/AuthCallbackPage'
import DashboardPage from './pages/DashboardPage'
import UsersPage from './pages/UsersPage'
import PhysiciansPage from './pages/PhysiciansPage'
import PhysicianDetailPage, { SharedPhysicianDetailPage } from './pages/PhysicianDetailPage'
import PharmacyPage from './pages/PharmacyPage'
import PharmacyDetailPage from './pages/PharmacyDetailPage'
import DrugProgramsPage from './pages/DrugProgramsPage'
import DrugProgramDetailPage from './pages/DrugProgramDetailPage'
import ProgramCategoriesPage from './pages/ProgramCategoriesPage'
import ProgramCategoryDetailPage from './pages/ProgramCategoryDetailPage'
import ReleasesPage from './pages/ReleasesPage'
import AnalyticsPage from './pages/AnalyticsPage'
import QueuesPage from './pages/QueuesPage'
import OrderFlowsPage from './pages/OrderFlowsPage'
import MarketingPage from './pages/MarketingPage'
import TrafficPage from './pages/TrafficPage'
import AttributionPage from './pages/AttributionPage'
import LiftPage from './pages/LiftPage'
import ChannelPerformancePage from './pages/ChannelPerformancePage'
import ProductByChannelPage from './pages/ProductByChannelPage'
import SlackPage from './pages/SlackPage'
import DailyReportPage from './pages/DailyReportPage'
import ScienceModelPage from './pages/ScienceModelPage'
import VelocityPage from './pages/VelocityPage'
import SalesTeamPage from './pages/SalesTeamPage'
import ProductMarketingPage from './pages/ProductMarketingPage'
import PaymentsPage from './pages/PaymentsPage'
import DeclinesPage from './pages/DeclinesPage'
import CfoReportPage from './pages/CfoReportPage'
import NewCustomersPage from './pages/NewCustomersPage'
import OrderExportPage from './pages/OrderExportPage'
import BundleReportPage from './pages/BundleReportPage'
import ZendeskPage from './pages/ZendeskPage'
import ApiKeysPage from './pages/ApiKeysPage'
import McpAccessPage from './pages/McpAccessPage'
import CodexPage from './pages/CodexPage'
import CodexAuditPage from './pages/CodexAuditPage'
import StatusPage from './pages/StatusPage'
import FileStoragePage from './pages/FileStoragePage'
import LoadingPage from './pages/LoadingPage'
import TVDisplayPage from './pages/TVDisplayPage'
import ChurnPage from './pages/ChurnPage'
import DemographicsPage from './pages/DemographicsPage'
import AppointmentsPage from './pages/AppointmentsPage'
import LanesPage from './pages/LanesPage'
import CrossSellPage from './pages/CrossSellPage'
import ConfigDiffPage from './pages/ConfigDiffPage'
import MigrationAuditPage from './pages/MigrationAuditPage'
import CampaignGoalsPage from './pages/CampaignGoalsPage'
import HealthDataPage from './pages/HealthDataPage'
import DisqualifiesPage from './pages/DisqualifiesPage'
import TransactionHealthPage from './pages/TransactionHealthPage'
import SubscriptionOverviewPage from './pages/SubscriptionOverviewPage'
import HsSubscriptionsPage from './pages/HsSubscriptionsPage'
import BnplRenewalsPage from './pages/BnplRenewalsPage'
import ProviderMessagingPage from './pages/ProviderMessagingPage'
import HsTransactionsPage from './pages/HsTransactionsPage'
import ApiLogsPage from './pages/ApiLogsPage'
import ProductCatalogPage from './pages/ProductCatalogPage'
import CouponsPage from './pages/CouponsPage'
import EmailTemplatesPage from './pages/EmailTemplatesPage'
import WeeklyReportPage from './pages/WeeklyReportPage'
import ExpressSetupPage from './pages/ExpressSetupPage'
import ExpressPortalsPage from './pages/ExpressPortalsPage'
// TEMPORARY: remove after the Firebase -> core portal auth cutover completes
import PortalAuthPage from './pages/PortalAuthPage'
import PortalImpersonationsPage from './pages/PortalImpersonationsPage'
import RoutingPage from './pages/RoutingPage'
import AdLpAlignmentPage from './pages/AdLpAlignmentPage'
import UserJourneyPage from './pages/UserJourneyPage'
import EngagementPage from './pages/EngagementPage'
import LpClickRatePage from './pages/LpClickRatePage'
import DealerTreePage from './pages/DealerTreePage'
import PharmacyCountPage from './pages/PharmacyCountPage'
import PrescribersPage from './pages/PrescribersPage'
import SharedPhysiciansPage from './pages/SharedPhysiciansPage'
import RewardsPage from './pages/RewardsPage'
import PharmacyBenefitPage from './pages/PharmacyBenefitPage'
import ProgressBoostPage from './pages/ProgressBoostPage'
import AccountingPage from './pages/AccountingPage'
import MultiPnLPage from './pages/MultiPnLPage'
import RefundsPage from './pages/RefundsPage'

// Components
import Layout from './components/Layout'
import ProtectedRoute from './components/ProtectedRoute'
import NewVersionNotice from './components/NewVersionNotice'

function App() {
  const { user, isLoading, initialize, isAuthenticated } = useAuthStore()
  const [appInitialized, setAppInitialized] = useState(false)

  // Initialize authentication state on app start with a small delay
  // to allow persist middleware to hydrate first
  useEffect(() => {
    const initializeApp = async () => {
      // Small delay to let Zustand persist middleware hydrate
      await new Promise(resolve => setTimeout(resolve, 100))
      await initialize()
      setAppInitialized(true)
    }
    
    initializeApp()
  }, [initialize])

  // Show loading screen while checking authentication or app is initializing
  if (!appInitialized || isLoading) {
    return <LoadingPage />
  }

  return (
    <>
    <NewVersionNotice />
    <Routes>
      {/* Public routes */}
      <Route 
        path="/login" 
        element={!isLoading && user ? <Navigate to="/" replace /> : <LoginPage />} 
      />
      
      {/* OAuth callback route */}
      <Route path="/auth/callback" element={<AuthCallbackPage />} />

      {/* TV Display route - full screen, no layout */}
      <Route
        path="/tv"
        element={
          <ProtectedRoute>
            <TVDisplayPage />
          </ProtectedRoute>
        }
      />

      {/* Protected routes with Layout */}
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <Layout>
              <Routes>
                <Route path="/" element={<DashboardPage />} />
                <Route path="/users" element={<UsersPage />} />
                <Route path="/physicians" element={<PhysiciansPage />} />
                <Route path="/shared-doctors" element={<SharedPhysiciansPage />} />
                <Route path="/shared-doctors/:id" element={<SharedPhysicianDetailPage />} />
                <Route path="/physicians/:id" element={<PhysicianDetailPage />} />
                <Route path="/pharmacy" element={<PharmacyPage />} />
                <Route path="/pharmacy/:id" element={<PharmacyDetailPage />} />
                <Route path="/drug-programs" element={<DrugProgramsPage />} />
                <Route path="/drug-programs/:id" element={<DrugProgramDetailPage />} />
                <Route path="/program-categories" element={<ProgramCategoriesPage />} />
                <Route path="/program-categories/:id" element={<ProgramCategoryDetailPage />} />
                <Route path="/releases" element={<ReleasesPage />} />
                {/* Reports section */}
                <Route path="/reports/pnl" element={<AnalyticsPage />} />
                <Route path="/reports/marketing" element={<MarketingPage />} />
                <Route path="/reports/traffic" element={<TrafficPage />} />
                <Route path="/reports/attribution" element={<AttributionPage />} />
                <Route path="/reports/lift" element={<LiftPage />} />
                <Route path="/reports/channels" element={<ChannelPerformancePage />} />
                <Route path="/reports/product-by-channel" element={<ProductByChannelPage />} />
                <Route path="/reports/daily" element={<DailyReportPage />} />
                <Route path="/reports/model-vs-actual" element={<ScienceModelPage />} />
                <Route path="/reports/velocity" element={<VelocityPage />} />
                <Route path="/reports/sales-team" element={<SalesTeamPage />} />
                <Route path="/admin/slack" element={<SlackPage />} />
                <Route path="/reports/product-marketing" element={<ProductMarketingPage />} />
                <Route path="/reports/churn" element={<ChurnPage />} />
                <Route path="/reports/demographics" element={<DemographicsPage />} />
                <Route path="/reports/lanes" element={<LanesPage />} />
                <Route path="/reports/cross-sell" element={<CrossSellPage />} />
                <Route path="/reports/ad-lp-alignment" element={<AdLpAlignmentPage />} />
                <Route path="/reports/user-journey" element={<UserJourneyPage />} />
                <Route path="/reports/engagement" element={<EngagementPage />} />
                <Route path="/reports/lp-click-rate" element={<LpClickRatePage />} />
                <Route path="/reports/dealer-tree" element={<DealerTreePage />} />
                <Route path="/reports/prescribers" element={<PrescribersPage />} />
                <Route path="/reports/pharmacy-benefit" element={<PharmacyBenefitPage />} />
                <Route path="/reports/progress-boost" element={<ProgressBoostPage />} />
                {/* Accounting section */}
                <Route path="/accounting/pharmacy-cost" element={<AccountingPage />} />
                <Route path="/accounting/multi-pnl" element={<MultiPnLPage />} />
                <Route path="/accounting/refunds" element={<RefundsPage />} />
                {/* Support section */}
                <Route path="/support/zendesk" element={<ZendeskPage />} />
                <Route path="/support/appointments" element={<AppointmentsPage />} />
                <Route path="/support/health-data" element={<HealthDataPage />} />
                <Route path="/support/disqualifies" element={<DisqualifiesPage />} />
                {/* Redirects from old paths */}
                <Route path="/reports/support" element={<Navigate to="/support/zendesk" replace />} />
                <Route path="/reports/appointments" element={<Navigate to="/support/appointments" replace />} />
                {/* Operations section */}
                <Route path="/ops/payments" element={<PaymentsPage />} />
                <Route path="/ops/declines" element={<DeclinesPage />} />
                <Route path="/ops/cfo" element={<CfoReportPage />} />
                <Route path="/ops/new-customers" element={<NewCustomersPage />} />
                <Route path="/ops/order-export" element={<OrderExportPage />} />
                <Route path="/ops/bundle-report" element={<BundleReportPage />} />
                <Route path="/ops/queues" element={<QueuesPage />} />
                <Route path="/ops/provider-messaging" element={<ProviderMessagingPage />} />
                <Route path="/ops/order-flows" element={<OrderFlowsPage />} />
                <Route path="/ops/routing" element={<RoutingPage />} />
                <Route path="/ops/pharmacy-count" element={<PharmacyCountPage />} />
                <Route path="/ops/rewards" element={<RewardsPage />} />
                <Route path="/ops/transaction-health" element={<TransactionHealthPage />} />
                <Route path="/ops/subscriptions" element={<SubscriptionOverviewPage />} />
                <Route path="/ops/hs-subscriptions" element={<HsSubscriptionsPage />} />
                <Route path="/ops/bnpl-renewals" element={<BnplRenewalsPage />} />
                <Route path="/ops/hs-transactions" element={<HsTransactionsPage />} />
                <Route path="/ops/api-logs" element={<ApiLogsPage />} />
                <Route path="/ops/catalog" element={<ProductCatalogPage />} />
                <Route path="/ops/coupons" element={<CouponsPage />} />
                <Route path="/ops/email-templates" element={<EmailTemplatesPage />} />
                {/* Redirects from old paths */}
                <Route path="/payments" element={<Navigate to="/ops/payments" replace />} />
                <Route path="/reports/queues" element={<Navigate to="/ops/queues" replace />} />
                <Route path="/reports/order-flows" element={<Navigate to="/ops/order-flows" replace />} />
                {/* Admin routes */}
                <Route path="/admin/api-keys" element={<ApiKeysPage />} />
                <Route path="/admin/mcp" element={<McpAccessPage />} />
                <Route path="/admin/codex" element={<CodexPage />} />
                <Route path="/admin/codex-audit" element={<CodexAuditPage />} />
                <Route path="/admin/status" element={<StatusPage />} />
                <Route path="/admin/file-storage" element={<FileStoragePage />} />
                <Route path="/admin/config-diff" element={<ConfigDiffPage />} />
                <Route path="/admin/migration-audit" element={<MigrationAuditPage />} />
                <Route path="/admin/campaign-goals" element={<CampaignGoalsPage />} />
                <Route path="/admin/weekly-report" element={<WeeklyReportPage />} />
                <Route path="/admin/express-setup" element={<ExpressSetupPage />} />
                <Route path="/admin/express-portals" element={<ExpressPortalsPage />} />
                <Route path="/admin/portal-auth" element={<PortalAuthPage />} />
                <Route path="/admin/portal-impersonations" element={<PortalImpersonationsPage />} />
                {/* Redirect old /analytics to new path */}
                <Route path="/analytics" element={<Navigate to="/reports/pnl" replace />} />
                <Route path="/clinic/:clinicId/*" element={<div>Clinic Routes (Coming Soon)</div>} />
                {/* 404 page without auto-redirect */}
                <Route path="*" element={
                  <div className="min-h-screen bg-background flex items-center justify-center">
                    <div className="text-center">
                      <h3 className="text-lg font-medium text-foreground mb-2">Page Not Found</h3>
                      <p className="text-muted-foreground mb-4">The page you're looking for doesn't exist.</p>
                      <button 
                        onClick={() => window.location.href = '/'}
                        className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
                      >
                        Go to Dashboard
                      </button>
                    </div>
                  </div>
                } />
              </Routes>
            </Layout>
          </ProtectedRoute>
        }
      />
    </Routes>
    </>
  )
}

export default App
